export const enum SurgeToggleMode {
	DISABLED = 0,
	ENABLED = 1,
	TIMED_BETA_ENABLED = 2,
	ENABLED_WITH_COOKIE = 3,
}

export const enum PokerLobbyToggleMode {
	DISABLED = 0,
	ENABLED = 1,
	ENABLED_WITH_COOKIE = 2,
}

export const enum BadBeatJackpotToggleMode {
	DISABLED = 0,
	ENABLED = 1,
	STAFF_ONLY = 2
}

export type ToggleMode = { mode: 'DISABLED' } | { mode: 'ENABLED' } | { mode: 'SET_USER_LIST'; users: number[] }

export type BlockedMarket = {
	isoEntry: string
	isoCountry: string
	countryName: string
	regionName: string
	ianaTimeZone: string
	knownEntries: string[]
	blockDate: number
	blockAllPlayAfterDays: number
	blockLoginAfterDays: number
}
export type PromoPlayBlockedMarket = {
	isoEntry: string
	isoCountry: string
	countryName: string
	regionName: string
	ianaTimeZone: string
	knownEntries: string[]
	blockDate: number
	blockAllPlayAfterDays: number
} & (
	| { blockRedemptionsAfterDays: number; blockRedemptionsDate?: never }
	// Use when Phase 3 must start at a wall-clock time that a day offset from blockDate can't reach
	| { blockRedemptionsDate: number; blockRedemptionsAfterDays?: never }
)
export type GcOnlyMarket = { isoEntry: string; knownEntries: string[]; blockDate: number }
export type CasinoOnlyBlockedMarket = { isoEntry: string; knownEntries: string[]; blockDate: number }
export type Market = BlockedMarket | GcOnlyMarket | PromoPlayBlockedMarket | CasinoOnlyBlockedMarket

const INDIANA_CASINO_ONLY_BLOCKED_MARKET: CasinoOnlyBlockedMarket = {
	isoEntry: 'IN',
	knownEntries: ['in', 'us-in', 'indiana'],
	blockDate: Date.UTC(2026, 5, 30, 6, 30),
}

export interface FeatureConfig {
	DAILY_REWARDS_PRIZE_DRAW_FEATURE: {
		testAnimationEnabled: boolean
		twoStepFlow: boolean
	}
	DAILY_BONUS_EXPERIMENT: {
		enabled: boolean
		experimentStartDateTime: number
		experimentDays: number
		registrationGracePeriodDays: number
		userEligibilityWindowDays: number
	}
	SURGE_FEATURE: {
		mode: SurgeToggleMode
		SURGE_URL: string
	}
	IS_GLOBAL_CASINO_ENABLED: boolean
	PRIVATE_GAMES_FEATURE: {
		url: string
		organiserFee: string
		expiryConfig: {
			expiryPeriod: number
			expiryPeriodUnit: 'minutes' | 'hours' | 'days'
		}
	}
	AMOE_CDD_ENABLED: boolean
	SELF_EXCLUSIONS_PLAYER_ACCOUNT_ENABLED: boolean
	GAP_HUD_ENABLED: boolean
	POKER_SERVER_TOURNAMENTS: {
		SNG_ENABLED: boolean
		PLAY_DURING_DOWNTIME_ENABLED: boolean
		// For more information please see https://github.com/VGW/gp-game-client/blob/main/docs/new-poker-server/new-poker-server.md
		USE_NEW_POKER_SERVER_ONLY: boolean
		TIMEOUTS_ENABLED: boolean
		HARDCODED_PLAYER_DETAILS: boolean
	}
	MFA_ENABLED: boolean
	PRIVACY_CHOICE_ENABLED: boolean
	BLOCKED_MARKETS: BlockedMarket[]
	PROMO_PLAY_BLOCKED_MARKETS_ENABLED: boolean
	PROMO_PLAY_BLOCKED_MARKETS: PromoPlayBlockedMarket[]
	GEOBLOCKED_MARKETS: BlockedMarket[]
	GC_ONLY_MARKETS: GcOnlyMarket[]
	CASINO_ONLY_BLOCKED_MARKETS: CasinoOnlyBlockedMarket[]
	BANK_VERIFICATION: {
		enabled: boolean
		useCanary: boolean
		users: number[]
	}
	USE_MIGRATED_PLAYER_NOTES: boolean
	ANIMATED_DAILY_BONUS_ENABLED: boolean
	REDEEMABLE_BALANCE: {
		enabled: boolean
		betaGroupCubeiaIds: number[]
		excludedGroupUserIds: number[]
		canarySeed: number
		canaryPercent: number
	}
	MOBILE_BANNER_EXPERIMENT: {
		enabled: boolean
		useCanary: boolean
		users: number[]
	}
	NEW_DAILY_REWARD_FEATURE_ENABLED: boolean
	ENABLE_SNG_TABLES_TAB: boolean
	BRAZE_FEATURES: {
		POKER_BANNER_ENABLED: boolean
		CASINO_BANNER_ENABLED: boolean
		LOGO_BANNER_ENABLED: boolean
		IN_APP_MESSAGES_ENABLED: boolean
		INBOX_ENABLED: boolean
		TOURNAMENT_FILTER_DEEP_LINK_ENABLED: boolean
		SNG_DEEP_LINK_ENABLED: boolean
		JSNG_DEEP_LINK_ENABLED: boolean
		RING_GAME_DEEP_LINK_ENABLED: boolean
	}
	POKER_LOBBY_GATEWAY: {
		mode: PokerLobbyToggleMode
		sngEnabled: boolean
	}
	POKER_LOBBY_PLAYER_GAMES: {
		mode: PokerLobbyToggleMode
	}
	POKER_LOBBY_INSTANCE: {
		mode: PokerLobbyToggleMode
	}
	GAP_BJK_PREMIUM_GAMEPLAY_HISTORY_ENABLED: boolean
	SLOTS_CLOSE_BUTTON_POSITION_ENABLED: boolean
	SLOT_GAMES_CLOSE_BUTTON_POSITION_ENABLED: string[]
	PLAYER_FEATURE_CONFIG: {
		enabled: boolean
		phase1UserIds: string[]
		phase2UserIds: string[]
		phase3UserIds: string[]
	}
	POKER_SERVER_RING_GAMES_ENABLED: boolean
	POKER_SERVER_RING_GAMES_PLAY_DURING_DOWNTIME_ENABLED: boolean
	CHALLENGE_CONFIG_RELEASE_AUTOMATION: {
		imageBaseUrl: string
	}
	FIND_GAME: {
		enabled: boolean
		loggingEnabled: boolean
	}
	RECENTLY_PLAYED_FEATURE_ENABLED: boolean
	FIRST_TIME_OFFER_ENABLED: boolean
	PARTIAL_REDEMPTIONS_ENABLED: boolean
	PAZE_PAYMENT_ENABLED: boolean
	GAP_IN_HOUSE_GAMES_ENABLED: boolean
	CLIENT_TRIGGERS: {
		firstGameExitTriggerEnabled: boolean
		cashierModalOpenedTriggerEnabled: boolean
	}
	POKER_CHALLENGE_UI_DISABLED: boolean
	GAP_CARIBBEAN_POKER_GAME_HISTORY_ENABLED: boolean
	DELAWARE_MARKET_EXIT_MODAL_ENABLED: boolean
	REACT_GAME_TABS_ENABLED: boolean
	GAP_GAME_TILE_ASSET_ENABLED: boolean
	POKER_LOBBY_SORTING: boolean
	GC_ONLY_MARKETS_CHALLENGE_FILTER_ENABLED: boolean
	HIGH_HAND_CHALLENGE_ENABLED: boolean
	POKER_SERVER_SCHEDULED_TOURNAMENTS_ENABLED: boolean
	SOCIAL_LIVE_CASINO_LIVESTAT_FEATURE_ENABLED: boolean
	FULL_MARKET_EXIT_MODALS_ENABLED: boolean
	SOCIAL_LIVE_CASINO_LIVESTAT_SHOW_MINIMUM_LIMIT: boolean
	ALL_IN_OR_FOLD_ENABLED: boolean
	BRAZE_DAILY_BONUS_CUSTOM_EVENT_FEATURE_ENABLED: boolean
	REFER_A_FRIEND_MENU_ENABLED: boolean
	REFERRAL_PROGRAM_ENABLED: boolean
	TIERED_REFERRALS_ENABLED: boolean
	REFERRAL_NUDGE_ENABLED: boolean
	BBJ_FEATURE: {
		mode: BadBeatJackpotToggleMode
		staffUserIds: number[]
		staffRingGamePrefix: string // Prevents staff bbj ring games from showing up before the table is marked as bbj
	}
	BBJ_PREVIOUS_LEADERBOARDS_ENABLED: boolean
	KYC_FIRST_PURCHASE_NEW_ENDPOINT_ENABLED: boolean
	TOOLBAR_V2_ENABLED: boolean
	LOBBY_SKELETON_ENABLED: boolean
	SC_BLOCKED_US_MARKETS: string[]
	KYC_FIRST_PURCHASE_NEW_FLOW_ENABLED: boolean
	GEO_COMPLY: {
		canaryPercent: number
		canarySeed: number
		betaGroupUserIds: number[]
		excludedGroupUserIds: number[]
	}
	GEO_GATE_REDIRECT_DELAY_MS: number
	GEO_SDK_TIMEOUT_MS: number
	GEO_LOCATE_DEVICE_TIMEOUT_MS: number
	GEO_HOLD_TIME_LIMIT_MS?: number
	GEO_CALLBACK_FAILED_THRESHOLD: number
	GEO_CALLBACK_RESTRICTED_THRESHOLD: number
	GEO_CALLBACK_ALLOWED_THRESHOLD: number
	GEO_COMPLY_SPOOF_USER_IDS: string[]
	GEO_COMPLY_SPOOF_EMAIL_PATTERN: string
	CDD_RESIDENTIAL_ADDRESS_ENABLED: boolean
	CASINO_EXIT_SERVER_CHECK_ENABLED: boolean
	/** When true, /geo_debug path shows the geo-debug panel. Off in prod. */
	GEO_DEBUG_PANEL_ENABLED: boolean
	/** When true, simulation mode activates on login, silencing SDK callbacks. Off by default. */
	GEO_SIMULATION_ENABLED: boolean
	GEO_CUSTOM_GUIDANCE_ENABLED: boolean
	GEO_PROTECTION_ENABLED: boolean
	JSNG_LEADERBOARDS_ENABLED: boolean
	JSNG_SC_MICRO_TIER_ENABLED: boolean
	JSNG_PRIZE_HISTORY_ENDPOINT_MIGRATION_ENABLED: boolean
	JSNG_RECENT_JACKPOTS_ENABLED: boolean
	JSNG_SC5_MULTIPLIER_AND_PRIZE_STRUCTURE_ENABLED: boolean
}

export const localFeatureConfig: FeatureConfig = {
	DAILY_REWARDS_PRIZE_DRAW_FEATURE: {
		testAnimationEnabled: true,
		twoStepFlow: true,
	},
	DAILY_BONUS_EXPERIMENT: {
		enabled: true,
		experimentStartDateTime: Date.parse('2025-09-15T12:00:00.000Z'),
		experimentDays: 14,
		registrationGracePeriodDays: 8,
		userEligibilityWindowDays: 7,
	},
	SURGE_FEATURE: {
		mode: SurgeToggleMode.DISABLED,
		SURGE_URL: 'http://localhost:10002',
	},
	IS_GLOBAL_CASINO_ENABLED: true,
	PRIVATE_GAMES_FEATURE: {
		url: 'http://localhost:10003',
		organiserFee: '100',
		expiryConfig: {
			expiryPeriod: 7,
			expiryPeriodUnit: 'days',
		},
	},
	AMOE_CDD_ENABLED: true,
	SELF_EXCLUSIONS_PLAYER_ACCOUNT_ENABLED: false, // disabled until Player Account is added to gp-docker
	GAP_HUD_ENABLED: true,
	POKER_SERVER_TOURNAMENTS: {
		SNG_ENABLED: true,
		PLAY_DURING_DOWNTIME_ENABLED: true,
		USE_NEW_POKER_SERVER_ONLY: true,
		TIMEOUTS_ENABLED: false,
		HARDCODED_PLAYER_DETAILS: true,
	},
	MFA_ENABLED: true,
	PRIVACY_CHOICE_ENABLED: true,
	BLOCKED_MARKETS: [],
	PROMO_PLAY_BLOCKED_MARKETS_ENABLED: true,
	PROMO_PLAY_BLOCKED_MARKETS: [
		{
			isoEntry: 'CA',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'California',
			ianaTimeZone: 'America/Los_Angeles',
			knownEntries: ['ca', 'us-ca', 'california'],
			blockDate: Date.parse('2025-12-30T04:30:00.000-08:00'),
			blockAllPlayAfterDays: 0,
			blockRedemptionsAfterDays: 16,
		},
		{
			isoEntry: 'NY',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'New York',
			ianaTimeZone: 'America/New_York',
			knownEntries: ['ny', 'us-ny', 'new york'],
			blockDate: Date.parse('2025-06-02T02:30:00.000-04:00'),
			blockAllPlayAfterDays: 30,
			blockRedemptionsAfterDays: 60,
		},
		{
			isoEntry: 'LA',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'Louisiana',
			ianaTimeZone: 'America/Chicago',
			knownEntries: ['la', 'us-la', 'louisiana'],
			blockDate: Date.parse('2025-07-17T20:30:00.000-05:00'),
			blockAllPlayAfterDays: 29,
			blockRedemptionsAfterDays: 57,
		},
		{
			isoEntry: 'NJ',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'New Jersey',
			ianaTimeZone: 'America/New_York',
			knownEntries: ['nj', 'us-nj', 'new jersey'],
			blockDate: Date.parse('2025-07-29T02:30:00.000-04:00'),
			blockAllPlayAfterDays: 28,
			blockRedemptionsAfterDays: 57,
		},
		{
			isoEntry: 'MS',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'Mississippi',
			ianaTimeZone: 'America/Chicago',
			knownEntries: ['ms', 'us-ms', 'mississippi'],
			blockDate: Date.parse('2025-07-31T02:30:00.000-04:00'),
			blockAllPlayAfterDays: 14,
			blockRedemptionsAfterDays: 35,
		},
		{
			isoEntry: 'WV',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'West Virginia',
			ianaTimeZone: 'America/New_York',
			knownEntries: ['wv', 'us-wv', 'west virginia'],
			blockDate: Date.parse('2025-11-11T02:30:00.000-04:00'),
			blockAllPlayAfterDays: 7,
			blockRedemptionsAfterDays: 14,
		},
		{
			isoEntry: 'TN',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'Tennessee',
			ianaTimeZone: 'America/Chicago',
			knownEntries: ['tn', 'us-tn', 'tennessee'],
			blockDate: Date.parse('2025-11-24T00:30:00.000-06:00'),
			blockAllPlayAfterDays: 29,
			blockRedemptionsAfterDays: 57,
		},
		{
			isoEntry: 'ME',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'Maine',
			ianaTimeZone: 'America/New_York',
			knownEntries: ['me', 'us-me', 'maine'],
			blockDate: Date.parse('2026-07-13T02:30:00.000-04:00'),
			blockAllPlayAfterDays: 0,
			blockRedemptionsAfterDays: 29,
		},
		{
			isoEntry: 'OK',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'Oklahoma',
			ianaTimeZone: 'America/Chicago',
			knownEntries: ['ok', 'us-ok', 'oklahoma'],
			blockDate: Date.parse('2026-10-30T01:30:00.000-05:00'),
			blockAllPlayAfterDays: 0,
			blockRedemptionsDate: Date.parse('2026-11-26T00:00:00.000-06:00'),
		},
	],
	GEOBLOCKED_MARKETS: [],
	GC_ONLY_MARKETS: [
		{
			isoEntry: 'ID',
			knownEntries: ['id', 'us-id', 'idaho'],
			blockDate: Date.UTC(2022, 8, 1, 0, 0),
		},
	],
	CASINO_ONLY_BLOCKED_MARKETS: [INDIANA_CASINO_ONLY_BLOCKED_MARKET],
	BANK_VERIFICATION: {
		enabled: true,
		useCanary: false,
		users: [90626],
	},
	USE_MIGRATED_PLAYER_NOTES: false,
	ANIMATED_DAILY_BONUS_ENABLED: false,
	REDEEMABLE_BALANCE: {
		enabled: true,
		betaGroupCubeiaIds: [],
		excludedGroupUserIds: [],
		canarySeed: 0,
		canaryPercent: 0,
	},
	MOBILE_BANNER_EXPERIMENT: {
		enabled: true,
		useCanary: true,
		users: [],
	},
	NEW_DAILY_REWARD_FEATURE_ENABLED: true,
	BRAZE_FEATURES: {
		POKER_BANNER_ENABLED: false,
		CASINO_BANNER_ENABLED: false,
		LOGO_BANNER_ENABLED: false,
		IN_APP_MESSAGES_ENABLED: false,
		INBOX_ENABLED: false,
		TOURNAMENT_FILTER_DEEP_LINK_ENABLED: false,
		SNG_DEEP_LINK_ENABLED: false,
		JSNG_DEEP_LINK_ENABLED: false,
		RING_GAME_DEEP_LINK_ENABLED: false,
	},
	ENABLE_SNG_TABLES_TAB: true,
	POKER_LOBBY_GATEWAY: {
		mode: PokerLobbyToggleMode.ENABLED,
		sngEnabled: true,
	},
	POKER_LOBBY_PLAYER_GAMES: {
		mode: PokerLobbyToggleMode.ENABLED,
	},
	POKER_LOBBY_INSTANCE: {
		mode: PokerLobbyToggleMode.ENABLED,
	},
	GAP_BJK_PREMIUM_GAMEPLAY_HISTORY_ENABLED: false,
	SLOTS_CLOSE_BUTTON_POSITION_ENABLED: false,
	SLOT_GAMES_CLOSE_BUTTON_POSITION_ENABLED: [],
	PLAYER_FEATURE_CONFIG: {
		enabled: true,
		phase1UserIds: [],
		phase2UserIds: [],
		phase3UserIds: [],
	},
	POKER_SERVER_RING_GAMES_ENABLED: true,
	POKER_SERVER_RING_GAMES_PLAY_DURING_DOWNTIME_ENABLED: true,
	CHALLENGE_CONFIG_RELEASE_AUTOMATION: {
		imageBaseUrl: 'https://assets.ci.test.globalpoker.com',
	},
	FIND_GAME: {
		enabled: true,
		loggingEnabled: true,
	},
	RECENTLY_PLAYED_FEATURE_ENABLED: true,
	FIRST_TIME_OFFER_ENABLED: true,
	PARTIAL_REDEMPTIONS_ENABLED: false,
	PAZE_PAYMENT_ENABLED: true,
	GAP_IN_HOUSE_GAMES_ENABLED: false,
	CLIENT_TRIGGERS: {
		firstGameExitTriggerEnabled: false,
		cashierModalOpenedTriggerEnabled: false,
	},
	POKER_CHALLENGE_UI_DISABLED: true,
	GAP_CARIBBEAN_POKER_GAME_HISTORY_ENABLED: true,
	DELAWARE_MARKET_EXIT_MODAL_ENABLED: true,
	REACT_GAME_TABS_ENABLED: false,
	GAP_GAME_TILE_ASSET_ENABLED: true,
	POKER_LOBBY_SORTING: false,
	GC_ONLY_MARKETS_CHALLENGE_FILTER_ENABLED: true,
	HIGH_HAND_CHALLENGE_ENABLED: false,
	POKER_SERVER_SCHEDULED_TOURNAMENTS_ENABLED: true,
	SOCIAL_LIVE_CASINO_LIVESTAT_FEATURE_ENABLED: false,
	FULL_MARKET_EXIT_MODALS_ENABLED: true,
	SOCIAL_LIVE_CASINO_LIVESTAT_SHOW_MINIMUM_LIMIT: false,
	ALL_IN_OR_FOLD_ENABLED: true,
	BRAZE_DAILY_BONUS_CUSTOM_EVENT_FEATURE_ENABLED: false,
	REFER_A_FRIEND_MENU_ENABLED: true,
	REFERRAL_PROGRAM_ENABLED: true,
	TIERED_REFERRALS_ENABLED: true,
	REFERRAL_NUDGE_ENABLED: true,
	BBJ_FEATURE: {
		mode: BadBeatJackpotToggleMode.ENABLED,
		staffUserIds: [],
		staffRingGamePrefix: 'STAFF_BBJ',
	},
	BBJ_PREVIOUS_LEADERBOARDS_ENABLED: true,
	KYC_FIRST_PURCHASE_NEW_ENDPOINT_ENABLED: true,
	TOOLBAR_V2_ENABLED: false,
	LOBBY_SKELETON_ENABLED: true,
	SC_BLOCKED_US_MARKETS: [
		'US-ID',
		'US-NY',
		'US-LA',
		'US-NJ',
		'US-MS',
		'US-WV',
		'US-TN',
		'US-WA',
		'US-QC',
		'US-MI',
		'US-MT',
		'US-CT',
		'US-NV',
		'US-DE',
		'US-CA',
	],
	KYC_FIRST_PURCHASE_NEW_FLOW_ENABLED: false,
	GEO_COMPLY: {
		canaryPercent: 0,
		canarySeed: 0,
		betaGroupUserIds: [],
		excludedGroupUserIds: [],
	},
	GEO_GATE_REDIRECT_DELAY_MS: 5000,
	GEO_SDK_TIMEOUT_MS: 60000,
	GEO_LOCATE_DEVICE_TIMEOUT_MS: 120000,
	GEO_CALLBACK_FAILED_THRESHOLD: 1,
	GEO_CALLBACK_RESTRICTED_THRESHOLD: 1,
	GEO_CALLBACK_ALLOWED_THRESHOLD: 1,
	GEO_COMPLY_SPOOF_USER_IDS: [],
	GEO_COMPLY_SPOOF_EMAIL_PATTERN: '@vgw\\.co$',
	CDD_RESIDENTIAL_ADDRESS_ENABLED: true,
	CASINO_EXIT_SERVER_CHECK_ENABLED: false,
	GEO_DEBUG_PANEL_ENABLED: true,
	GEO_SIMULATION_ENABLED: false,
	GEO_CUSTOM_GUIDANCE_ENABLED: true,
	GEO_PROTECTION_ENABLED: false,
	JSNG_LEADERBOARDS_ENABLED: true,
	JSNG_SC_MICRO_TIER_ENABLED: true,
	JSNG_PRIZE_HISTORY_ENDPOINT_MIGRATION_ENABLED: true,
	JSNG_RECENT_JACKPOTS_ENABLED: true,
	JSNG_SC5_MULTIPLIER_AND_PRIZE_STRUCTURE_ENABLED: true,
}

export const ciFeatureConfig: FeatureConfig = {
	DAILY_REWARDS_PRIZE_DRAW_FEATURE: {
		testAnimationEnabled: false,
		twoStepFlow: false,
	},
	DAILY_BONUS_EXPERIMENT: {
		enabled: true,
		experimentStartDateTime: Date.parse('2025-09-15T12:00:00.000Z'),
		experimentDays: 14,
		registrationGracePeriodDays: 8,
		userEligibilityWindowDays: 7,
	},
	SURGE_FEATURE: {
		mode: SurgeToggleMode.DISABLED,
		SURGE_URL: 'https://surge-pool.ci.test.globalpoker.com',
	},
	IS_GLOBAL_CASINO_ENABLED: true,
	PRIVATE_GAMES_FEATURE: {
		url: 'https://private-game.ci.test.globalpoker.com',
		organiserFee: '100',
		expiryConfig: {
			expiryPeriod: 7,
			expiryPeriodUnit: 'days',
		},
	},
	AMOE_CDD_ENABLED: true,
	SELF_EXCLUSIONS_PLAYER_ACCOUNT_ENABLED: true,
	GAP_HUD_ENABLED: false,
	POKER_SERVER_TOURNAMENTS: {
		SNG_ENABLED: true,
		PLAY_DURING_DOWNTIME_ENABLED: true,
		USE_NEW_POKER_SERVER_ONLY: false,
		TIMEOUTS_ENABLED: true,
		HARDCODED_PLAYER_DETAILS: false,
	},
	MFA_ENABLED: true,
	PRIVACY_CHOICE_ENABLED: true,
	BLOCKED_MARKETS: [
		{
			isoEntry: 'MI',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'Michigan',
			ianaTimeZone: 'America/Detroit',
			knownEntries: ['mi', 'us-mi', 'michigan', 'michiga'],
			blockDate: Date.parse('2023-11-01T01:00:00.000-04:00'), // TZ for MI is EDT (UTC-04:00)
			blockAllPlayAfterDays: 30, // 2023-12-01T01:00:00.000-04:00
			blockLoginAfterDays: 92, // 2024-02-01T01:00:00.000-04:00
		},
		{
			isoEntry: 'MT',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'Montana',
			ianaTimeZone: 'America/Denver',
			knownEntries: ['mt', 'us-mt', 'montana'],
			blockDate: Date.parse('2024-01-23T00:00:00.000-07:00'), // TZ for MT is UTC-07:00
			blockAllPlayAfterDays: 30, // 2024-02-22T00:00:00.000-07:00
			blockLoginAfterDays: 90, // 2024-04-22T00:00:00.000-07:00
		},
		{
			isoEntry: 'CT',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'Connecticut',
			ianaTimeZone: 'America/New_York',
			knownEntries: ['ct', 'us-ct', 'connecticut'],
			blockDate: Date.parse('2024-09-16T00:00:00.000-04:00'), // TZ for CT is EDT (UTC-04:00)
			blockAllPlayAfterDays: 30, // 2024-10-16T00:00:00.000-04:00
			blockLoginAfterDays: 91, // 2024-12-16T00:00:00.000-05:00 (EDT ends 5/11/2024)
		},
		{
			isoEntry: 'NV',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'Nevada',
			ianaTimeZone: 'America/Los_Angeles',
			knownEntries: ['nv', 'us-nv', 'nevada'],
			blockDate: Date.parse('2025-01-15T00:00:00.000-08:00'), // TZ for NV is PST (UTC-08:00)
			blockAllPlayAfterDays:
				(Date.parse('2025-02-17T00:00:00.000-08:00') - Date.parse('2025-01-15T00:00:00.000-08:00')) /
				(1000 * 60 * 60 * 24), // 2025-02-17T00:00:00.000-08:00
			blockLoginAfterDays: 90, // 2025-04-15T23:00:00.000-08:00 (PDT starts 9/3/2025, shifts to UCT-0700)
		},
		{
			isoEntry: 'DE',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'Delaware',
			ianaTimeZone: 'America/New_York',
			knownEntries: ['de', 'us-de', 'delaware'],
			blockDate: Date.parse('2025-04-02T21:30:00.000-04:00'), // TZ for DE is EDT (UTC-04:00)
			blockAllPlayAfterDays: 0, // M1 and M2 exit at the same time
			blockLoginAfterDays: 111, // 2025-07-22T23:59:00.000-04:00
		},
		{
			isoEntry: 'AB',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Alberta',
			ianaTimeZone: 'America/Edmonton',
			knownEntries: ['ca-ab', 'ab', 'alberta'],
			blockDate: Date.parse('2025-08-28T01:30:00.000-06:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'BC',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'British Columbia',
			ianaTimeZone: 'America/Vancouver',
			knownEntries: ['ca-bc', 'bc', 'british columbia'],
			blockDate: Date.parse('2025-08-28T00:30:00.000-07:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'MB',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Manitoba',
			ianaTimeZone: 'America/Winnipeg',
			knownEntries: ['ca-mb', 'mb', 'manitoba'],
			blockDate: Date.parse('2025-08-28T02:30:00.000-05:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'NB',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'New Brunswick',
			ianaTimeZone: 'America/Halifax',
			knownEntries: ['ca-nb', 'nb', 'new brunswick'],
			blockDate: Date.parse('2025-08-28T04:30:00.000-03:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'NL',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Newfoundland and Labrador',
			ianaTimeZone: 'America/St_Johns',
			knownEntries: ['ca-nl', 'nl', 'newfoundland and labrador'],
			blockDate: Date.parse('2025-08-28T05:00:00.000-02:30'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'NT',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Northwest Territories',
			ianaTimeZone: 'America/Yellowknife',
			knownEntries: ['ca-nt', 'nt', 'northwest territories'],
			blockDate: Date.parse('2025-08-28T01:30:00.000-06:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'NS',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Nova Scotia',
			ianaTimeZone: 'America/Halifax',
			knownEntries: ['ca-ns', 'ns', 'nova scotia'],
			blockDate: Date.parse('2025-08-28T04:30:00.000-03:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'NU',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Nunavut',
			ianaTimeZone: 'America/Iqaluit',
			knownEntries: ['ca-nu', 'nu', 'nunavut'],
			blockDate: Date.parse('2025-08-28T03:30:00.000-04:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'ON',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Ontario',
			ianaTimeZone: 'America/Toronto',
			knownEntries: ['ca-on', 'on', 'ontario'],
			blockDate: Date.parse('2025-08-28T03:30:00.000-04:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'PE',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Prince Edward Island',
			ianaTimeZone: 'America/Halifax',
			knownEntries: ['ca-pe', 'pe', 'prince edward island'],
			blockDate: Date.parse('2025-08-28T04:30:00.000-03:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'QC',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Quebec',
			ianaTimeZone: 'America/Toronto',
			knownEntries: ['ca-qc', 'qc', 'quebec'],
			blockDate: Date.parse('2025-08-28T03:30:00.000-04:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'SK',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Saskatchewan',
			ianaTimeZone: 'America/Regina',
			knownEntries: ['ca-sk', 'sk', 'saskatchewan'],
			blockDate: Date.parse('2025-08-28T01:30:00.000-06:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'YT',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Yukon',
			ianaTimeZone: 'America/Whitehorse',
			knownEntries: ['ca-yt', 'yt', 'yukon'],
			blockDate: Date.parse('2025-08-28T00:30:00.000-07:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
	],
	PROMO_PLAY_BLOCKED_MARKETS_ENABLED: true,
	PROMO_PLAY_BLOCKED_MARKETS: [
		{
			isoEntry: 'CA',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'California',
			ianaTimeZone: 'America/Los_Angeles',
			knownEntries: ['ca', 'us-ca', 'california'],
			blockDate: Date.parse('2025-12-30T04:30:00.000-08:00'),
			blockAllPlayAfterDays: 0,
			blockRedemptionsAfterDays: 16,
		},
		{
			isoEntry: 'NY',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'New York',
			ianaTimeZone: 'America/New_York',
			knownEntries: ['ny', 'us-ny', 'new york'],
			blockDate: Date.parse('2025-06-02T02:30:00.000-04:00'),
			blockAllPlayAfterDays: 30,
			blockRedemptionsAfterDays: 60,
		},
		{
			isoEntry: 'LA',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'Louisiana',
			ianaTimeZone: 'America/Chicago',
			knownEntries: ['la', 'us-la', 'louisiana'],
			blockDate: Date.parse('2025-07-17T20:30:00.000-05:00'),
			blockAllPlayAfterDays: 29,
			blockRedemptionsAfterDays: 57,
		},
		{
			isoEntry: 'NJ',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'New Jersey',
			ianaTimeZone: 'America/New_York',
			knownEntries: ['nj', 'us-nj', 'new jersey'],
			blockDate: Date.parse('2025-07-29T02:30:00.000-04:00'),
			blockAllPlayAfterDays: 28,
			blockRedemptionsAfterDays: 57,
		},
		{
			isoEntry: 'MS',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'Mississippi',
			ianaTimeZone: 'America/Chicago',
			knownEntries: ['ms', 'us-ms', 'mississippi'],
			blockDate: Date.parse('2025-07-31T02:30:00.000-04:00'),
			blockAllPlayAfterDays: 14,
			blockRedemptionsAfterDays: 35,
		},
		{
			isoEntry: 'WV',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'West Virginia',
			ianaTimeZone: 'America/New_York',
			knownEntries: ['wv', 'us-wv', 'west virginia'],
			blockDate: Date.parse('2025-11-11T02:30:00.000-04:00'),
			blockAllPlayAfterDays: 7,
			blockRedemptionsAfterDays: 14,
		},
		{
			isoEntry: 'TN',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'Tennessee',
			ianaTimeZone: 'America/Chicago',
			knownEntries: ['tn', 'us-tn', 'tennessee'],
			blockDate: Date.parse('2025-11-24T00:30:00.000-06:00'),
			blockAllPlayAfterDays: 29,
			blockRedemptionsAfterDays: 57,
		},
		{
			isoEntry: 'ME',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'Maine',
			ianaTimeZone: 'America/New_York',
			knownEntries: ['me', 'us-me', 'maine'],
			blockDate: Date.parse('2026-07-13T02:30:00.000-04:00'),
			blockAllPlayAfterDays: 0,
			blockRedemptionsAfterDays: 29,
		},
		{
			isoEntry: 'OK',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'Oklahoma',
			ianaTimeZone: 'America/Chicago',
			knownEntries: ['ok', 'us-ok', 'oklahoma'],
			blockDate: Date.parse('2026-10-30T01:30:00.000-05:00'),
			blockAllPlayAfterDays: 0,
			blockRedemptionsDate: Date.parse('2026-11-26T00:00:00.000-06:00'),
		},
	],
	GEOBLOCKED_MARKETS: [
		{
			isoEntry: 'DE',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'Delaware',
			ianaTimeZone: 'America/New_York',
			knownEntries: ['de', 'us-de', 'delaware'],
			blockDate: Date.parse('2025-04-02T21:30:00.000-04:00'), // TZ for DE is EDT (UTC-04:00)
			blockAllPlayAfterDays: 0, // M1 and M2 exit at the same time
			blockLoginAfterDays: 111, // 2025-07-22T23:59:00.000-04:00
		},
		{
			isoEntry: 'AB',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Alberta',
			ianaTimeZone: 'America/Edmonton',
			knownEntries: ['ca-ab', 'ab', 'alberta'],
			blockDate: Date.parse('2025-08-28T01:30:00.000-06:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'BC',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'British Columbia',
			ianaTimeZone: 'America/Vancouver',
			knownEntries: ['ca-bc', 'bc', 'british columbia'],
			blockDate: Date.parse('2025-08-28T00:30:00.000-07:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'MB',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Manitoba',
			ianaTimeZone: 'America/Winnipeg',
			knownEntries: ['ca-mb', 'mb', 'manitoba'],
			blockDate: Date.parse('2025-08-28T02:30:00.000-05:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'NB',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'New Brunswick',
			ianaTimeZone: 'America/Halifax',
			knownEntries: ['ca-nb', 'nb', 'new brunswick'],
			blockDate: Date.parse('2025-08-28T04:30:00.000-03:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'NL',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Newfoundland and Labrador',
			ianaTimeZone: 'America/St_Johns',
			knownEntries: ['ca-nl', 'nl', 'newfoundland and labrador'],
			blockDate: Date.parse('2025-08-28T05:00:00.000-02:30'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'NT',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Northwest Territories',
			ianaTimeZone: 'America/Yellowknife',
			knownEntries: ['ca-nt', 'nt', 'northwest territories'],
			blockDate: Date.parse('2025-08-28T01:30:00.000-06:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'NS',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Nova Scotia',
			ianaTimeZone: 'America/Halifax',
			knownEntries: ['ca-ns', 'ns', 'nova scotia'],
			blockDate: Date.parse('2025-08-28T04:30:00.000-03:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'NU',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Nunavut',
			ianaTimeZone: 'America/Iqaluit',
			knownEntries: ['ca-nu', 'nu', 'nunavut'],
			blockDate: Date.parse('2025-08-28T03:30:00.000-04:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'ON',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Ontario',
			ianaTimeZone: 'America/Toronto',
			knownEntries: ['ca-on', 'on', 'ontario'],
			blockDate: Date.parse('2025-08-28T03:30:00.000-04:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'PE',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Prince Edward Island',
			ianaTimeZone: 'America/Halifax',
			knownEntries: ['ca-pe', 'pe', 'prince edward island'],
			blockDate: Date.parse('2025-08-28T04:30:00.000-03:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'QC',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Quebec',
			ianaTimeZone: 'America/Toronto',
			knownEntries: ['ca-qc', 'qc', 'quebec'],
			blockDate: Date.parse('2025-08-28T03:30:00.000-04:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'SK',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Saskatchewan',
			ianaTimeZone: 'America/Regina',
			knownEntries: ['ca-sk', 'sk', 'saskatchewan'],
			blockDate: Date.parse('2025-08-28T01:30:00.000-06:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'YT',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Yukon',
			ianaTimeZone: 'America/Whitehorse',
			knownEntries: ['ca-yt', 'yt', 'yukon'],
			blockDate: Date.parse('2025-08-28T00:30:00.000-07:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
	],
	GC_ONLY_MARKETS: [
		{
			isoEntry: 'ID',
			knownEntries: ['id', 'us-id', 'idaho'],
			blockDate: Date.UTC(2022, 8, 1, 0, 0),
		},
	],
	CASINO_ONLY_BLOCKED_MARKETS: [INDIANA_CASINO_ONLY_BLOCKED_MARKET],
	BANK_VERIFICATION: {
		enabled: true,
		useCanary: false,
		users: [90626],
	},
	USE_MIGRATED_PLAYER_NOTES: false,
	ANIMATED_DAILY_BONUS_ENABLED: false,
	REDEEMABLE_BALANCE: {
		enabled: true,
		betaGroupCubeiaIds: [179788],
		excludedGroupUserIds: [],
		canarySeed: 0,
		canaryPercent: 100,
	},
	MOBILE_BANNER_EXPERIMENT: {
		enabled: true,
		useCanary: true,
		users: [],
	},
	NEW_DAILY_REWARD_FEATURE_ENABLED: true,
	BRAZE_FEATURES: {
		POKER_BANNER_ENABLED: false,
		CASINO_BANNER_ENABLED: false,
		LOGO_BANNER_ENABLED: false,
		IN_APP_MESSAGES_ENABLED: false,
		INBOX_ENABLED: false,
		TOURNAMENT_FILTER_DEEP_LINK_ENABLED: false,
		SNG_DEEP_LINK_ENABLED: false,
		JSNG_DEEP_LINK_ENABLED: false,
		RING_GAME_DEEP_LINK_ENABLED: false,
	},
	ENABLE_SNG_TABLES_TAB: true,
	POKER_LOBBY_GATEWAY: {
		mode: PokerLobbyToggleMode.ENABLED,
		sngEnabled: true,
	},
	POKER_LOBBY_PLAYER_GAMES: {
		mode: PokerLobbyToggleMode.ENABLED,
	},
	POKER_LOBBY_INSTANCE: {
		mode: PokerLobbyToggleMode.ENABLED,
	},
	GAP_BJK_PREMIUM_GAMEPLAY_HISTORY_ENABLED: false,
	SLOTS_CLOSE_BUTTON_POSITION_ENABLED: false,
	SLOT_GAMES_CLOSE_BUTTON_POSITION_ENABLED: [],
	PLAYER_FEATURE_CONFIG: {
		enabled: true,
		phase1UserIds: [],
		phase2UserIds: [],
		phase3UserIds: [],
	},
	POKER_SERVER_RING_GAMES_ENABLED: true,
	POKER_SERVER_RING_GAMES_PLAY_DURING_DOWNTIME_ENABLED: true,
	CHALLENGE_CONFIG_RELEASE_AUTOMATION: {
		imageBaseUrl: 'https://assets.ci.test.globalpoker.com',
	},
	FIND_GAME: {
		enabled: true,
		loggingEnabled: true,
	},
	RECENTLY_PLAYED_FEATURE_ENABLED: true,
	FIRST_TIME_OFFER_ENABLED: true,
	PARTIAL_REDEMPTIONS_ENABLED: false,
	PAZE_PAYMENT_ENABLED: true,
	GAP_IN_HOUSE_GAMES_ENABLED: false,
	CLIENT_TRIGGERS: {
		firstGameExitTriggerEnabled: false,
		cashierModalOpenedTriggerEnabled: false,
	},
	POKER_CHALLENGE_UI_DISABLED: true,
	GAP_CARIBBEAN_POKER_GAME_HISTORY_ENABLED: true,
	DELAWARE_MARKET_EXIT_MODAL_ENABLED: true,
	REACT_GAME_TABS_ENABLED: false,
	GAP_GAME_TILE_ASSET_ENABLED: true,
	POKER_LOBBY_SORTING: false,
	GC_ONLY_MARKETS_CHALLENGE_FILTER_ENABLED: true,
	HIGH_HAND_CHALLENGE_ENABLED: false,
	POKER_SERVER_SCHEDULED_TOURNAMENTS_ENABLED: true,
	SOCIAL_LIVE_CASINO_LIVESTAT_FEATURE_ENABLED: true,
	FULL_MARKET_EXIT_MODALS_ENABLED: true,
	SOCIAL_LIVE_CASINO_LIVESTAT_SHOW_MINIMUM_LIMIT: false,
	ALL_IN_OR_FOLD_ENABLED: true,
	BRAZE_DAILY_BONUS_CUSTOM_EVENT_FEATURE_ENABLED: false,
	REFER_A_FRIEND_MENU_ENABLED: true,
	REFERRAL_PROGRAM_ENABLED: true,
	TIERED_REFERRALS_ENABLED: true,
	REFERRAL_NUDGE_ENABLED: true,
	BBJ_FEATURE: {
		mode: BadBeatJackpotToggleMode.ENABLED,
		staffUserIds: [],
		staffRingGamePrefix: 'STAFF_BBJ',
	},
	BBJ_PREVIOUS_LEADERBOARDS_ENABLED: true,
	KYC_FIRST_PURCHASE_NEW_ENDPOINT_ENABLED: true,
	TOOLBAR_V2_ENABLED: false,
	LOBBY_SKELETON_ENABLED: true,
	SC_BLOCKED_US_MARKETS: [
		'US-ID',
		'US-NY',
		'US-LA',
		'US-NJ',
		'US-MS',
		'US-WV',
		'US-TN',
		'US-WA',
		'US-QC',
		'US-MI',
		'US-MT',
		'US-CT',
		'US-NV',
		'US-DE',
		'US-CA',
	],
	KYC_FIRST_PURCHASE_NEW_FLOW_ENABLED: true,
	GEO_COMPLY: {
		canaryPercent: 0,
		canarySeed: 0,
		betaGroupUserIds: [],
		excludedGroupUserIds: [],
	},
	GEO_GATE_REDIRECT_DELAY_MS: 5000,
	GEO_SDK_TIMEOUT_MS: 60000,
	GEO_LOCATE_DEVICE_TIMEOUT_MS: 120000,
	GEO_CALLBACK_FAILED_THRESHOLD: 1,
	GEO_CALLBACK_RESTRICTED_THRESHOLD: 1,
	GEO_CALLBACK_ALLOWED_THRESHOLD: 1,
	GEO_COMPLY_SPOOF_USER_IDS: [],
	GEO_COMPLY_SPOOF_EMAIL_PATTERN: '@vgw\\.co$',
	CDD_RESIDENTIAL_ADDRESS_ENABLED: true,
	CASINO_EXIT_SERVER_CHECK_ENABLED: true,
	GEO_DEBUG_PANEL_ENABLED: true,
	GEO_SIMULATION_ENABLED: false,
	GEO_CUSTOM_GUIDANCE_ENABLED: true,
	GEO_PROTECTION_ENABLED: false,
	JSNG_LEADERBOARDS_ENABLED: true,
	JSNG_SC_MICRO_TIER_ENABLED: true,
	JSNG_PRIZE_HISTORY_ENDPOINT_MIGRATION_ENABLED: true,
	JSNG_RECENT_JACKPOTS_ENABLED: true,
	JSNG_SC5_MULTIPLIER_AND_PRIZE_STRUCTURE_ENABLED: true,
}

export const qcFeatureConfig: FeatureConfig = {
	DAILY_REWARDS_PRIZE_DRAW_FEATURE: {
		testAnimationEnabled: false,
		twoStepFlow: false,
	},
	DAILY_BONUS_EXPERIMENT: {
		enabled: true,
		experimentStartDateTime: Date.parse('2025-09-15T12:00:00.000Z'),
		experimentDays: 14,
		registrationGracePeriodDays: 8,
		userEligibilityWindowDays: 7,
	},
	SURGE_FEATURE: {
		mode: SurgeToggleMode.DISABLED,
		SURGE_URL: 'https://surge-pool.qc.test.globalpoker.com',
	},
	IS_GLOBAL_CASINO_ENABLED: true,
	PRIVATE_GAMES_FEATURE: {
		url: 'https://private-game.qc.test.globalpoker.com',
		organiserFee: '25,000',
		expiryConfig: {
			expiryPeriod: 10,
			expiryPeriodUnit: 'minutes',
		},
	},
	AMOE_CDD_ENABLED: true,
	SELF_EXCLUSIONS_PLAYER_ACCOUNT_ENABLED: true,
	GAP_HUD_ENABLED: false,
	POKER_SERVER_TOURNAMENTS: {
		SNG_ENABLED: true,
		PLAY_DURING_DOWNTIME_ENABLED: true,
		USE_NEW_POKER_SERVER_ONLY: false,
		TIMEOUTS_ENABLED: true,
		HARDCODED_PLAYER_DETAILS: false,
	},
	MFA_ENABLED: true,
	PRIVACY_CHOICE_ENABLED: true,
	BLOCKED_MARKETS: [
		{
			isoEntry: 'MI',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'Michigan',
			ianaTimeZone: 'America/Detroit',
			knownEntries: ['mi', 'us-mi', 'michigan', 'michiga'],
			blockDate: Date.parse('2023-11-01T01:00:00.000-04:00'), // TZ for MI is EDT (UTC-04:00)
			blockAllPlayAfterDays: 30, // 2023-12-01T01:00:00.000-04:00
			blockLoginAfterDays: 92, // 2024-02-01T01:00:00.000-04:00
		},
		{
			isoEntry: 'MT',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'Montana',
			ianaTimeZone: 'America/Denver',
			knownEntries: ['mt', 'us-mt', 'montana'],
			blockDate: Date.parse('2024-01-23T00:00:00.000-07:00'), // TZ for MT is UTC-07:00
			blockAllPlayAfterDays: 30, // 2024-02-22T00:00:00.000-07:00
			blockLoginAfterDays: 90, // 2024-04-22T00:00:00.000-07:00
		},
		{
			isoEntry: 'CT',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'Connecticut',
			ianaTimeZone: 'America/New_York',
			knownEntries: ['ct', 'us-ct', 'connecticut'],
			blockDate: Date.parse('2024-09-16T00:00:00.000-04:00'), // TZ for CT is EDT (UTC-04:00)
			blockAllPlayAfterDays: 30, // 2024-10-16T00:00:00.000-04:00
			blockLoginAfterDays: 91, // 2024-12-16T00:00:00.000-05:00 (EDT ends 5/11/2024)
		},
		{
			isoEntry: 'NV',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'Nevada',
			ianaTimeZone: 'America/Los_Angeles',
			knownEntries: ['nv', 'us-nv', 'nevada'],
			blockDate: Date.parse('2025-01-15T00:00:00.000-08:00'), // TZ for NV is PST (UTC-08:00)
			blockAllPlayAfterDays:
				(Date.parse('2025-02-17T00:00:00.000-08:00') - Date.parse('2025-01-15T00:00:00.000-08:00')) /
				(1000 * 60 * 60 * 24), // 2025-02-17T00:00:00.000-08:00
			blockLoginAfterDays: 90, // 2025-04-15T23:00:00.000-08:00 (PDT starts 9/3/2025, shifts to UCT-0700)
		},
		{
			isoEntry: 'DE',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'Delaware',
			ianaTimeZone: 'America/New_York',
			knownEntries: ['de', 'us-de', 'delaware'],
			blockDate: Date.parse('2025-04-02T21:30:00.000-04:00'), // TZ for DE is EDT (UTC-04:00)
			blockAllPlayAfterDays: 0, // M1 and M2 exit at the same time
			blockLoginAfterDays: 112, // 2025-07-23T21:30:00.000-04:00
		},
		{
			isoEntry: 'AB',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Alberta',
			ianaTimeZone: 'America/Edmonton',
			knownEntries: ['ca-ab', 'ab', 'alberta'],
			blockDate: Date.parse('2025-08-28T01:30:00.000-06:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'BC',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'British Columbia',
			ianaTimeZone: 'America/Vancouver',
			knownEntries: ['ca-bc', 'bc', 'british columbia'],
			blockDate: Date.parse('2025-08-28T00:30:00.000-07:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'MB',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Manitoba',
			ianaTimeZone: 'America/Winnipeg',
			knownEntries: ['ca-mb', 'mb', 'manitoba'],
			blockDate: Date.parse('2025-08-28T02:30:00.000-05:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'NB',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'New Brunswick',
			ianaTimeZone: 'America/Halifax',
			knownEntries: ['ca-nb', 'nb', 'new brunswick'],
			blockDate: Date.parse('2025-08-28T04:30:00.000-03:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'NL',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Newfoundland and Labrador',
			ianaTimeZone: 'America/St_Johns',
			knownEntries: ['ca-nl', 'nl', 'newfoundland and labrador'],
			blockDate: Date.parse('2025-08-28T05:00:00.000-02:30'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'NT',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Northwest Territories',
			ianaTimeZone: 'America/Yellowknife',
			knownEntries: ['ca-nt', 'nt', 'northwest territories'],
			blockDate: Date.parse('2025-08-28T01:30:00.000-06:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'NS',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Nova Scotia',
			ianaTimeZone: 'America/Halifax',
			knownEntries: ['ca-ns', 'ns', 'nova scotia'],
			blockDate: Date.parse('2025-08-28T04:30:00.000-03:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'NU',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Nunavut',
			ianaTimeZone: 'America/Iqaluit',
			knownEntries: ['ca-nu', 'nu', 'nunavut'],
			blockDate: Date.parse('2025-08-28T03:30:00.000-04:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'ON',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Ontario',
			ianaTimeZone: 'America/Toronto',
			knownEntries: ['ca-on', 'on', 'ontario'],
			blockDate: Date.parse('2025-08-28T03:30:00.000-04:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'PE',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Prince Edward Island',
			ianaTimeZone: 'America/Halifax',
			knownEntries: ['ca-pe', 'pe', 'prince edward island'],
			blockDate: Date.parse('2025-08-28T04:30:00.000-03:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'QC',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Quebec',
			ianaTimeZone: 'America/Toronto',
			knownEntries: ['ca-qc', 'qc', 'quebec'],
			blockDate: Date.parse('2025-08-28T03:30:00.000-04:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'SK',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Saskatchewan',
			ianaTimeZone: 'America/Regina',
			knownEntries: ['ca-sk', 'sk', 'saskatchewan'],
			blockDate: Date.parse('2025-08-28T01:30:00.000-06:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'YT',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Yukon',
			ianaTimeZone: 'America/Whitehorse',
			knownEntries: ['ca-yt', 'yt', 'yukon'],
			blockDate: Date.parse('2025-08-28T00:30:00.000-07:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
	],
	PROMO_PLAY_BLOCKED_MARKETS_ENABLED: true,
	PROMO_PLAY_BLOCKED_MARKETS: [
		{
			isoEntry: 'CA',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'California',
			ianaTimeZone: 'America/Los_Angeles',
			knownEntries: ['ca', 'us-ca', 'california'],
			blockDate: Date.parse('2025-12-30T04:30:00.000-08:00'),
			blockAllPlayAfterDays: 0,
			blockRedemptionsAfterDays: 16,
		},
		{
			isoEntry: 'NY',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'New York',
			ianaTimeZone: 'America/New_York',
			knownEntries: ['ny', 'us-ny', 'new york'],
			blockDate: Date.parse('2025-06-02T02:30:00.000-04:00'),
			blockAllPlayAfterDays: 30,
			blockRedemptionsAfterDays: 60,
		},
		{
			isoEntry: 'LA',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'Louisiana',
			ianaTimeZone: 'America/Chicago',
			knownEntries: ['la', 'us-la', 'louisiana'],
			blockDate: Date.parse('2025-07-17T20:30:00.000-05:00'),
			blockAllPlayAfterDays: 29,
			blockRedemptionsAfterDays: 57,
		},
		{
			isoEntry: 'NJ',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'New Jersey',
			ianaTimeZone: 'America/New_York',
			knownEntries: ['nj', 'us-nj', 'new jersey'],
			blockDate: Date.parse('2025-07-29T02:30:00.000-04:00'),
			blockAllPlayAfterDays: 28,
			blockRedemptionsAfterDays: 57,
		},
		{
			isoEntry: 'MS',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'Mississippi',
			ianaTimeZone: 'America/Chicago',
			knownEntries: ['ms', 'us-ms', 'mississippi'],
			blockDate: Date.parse('2025-07-31T02:30:00.000-04:00'),
			blockAllPlayAfterDays: 14,
			blockRedemptionsAfterDays: 35,
		},
		{
			isoEntry: 'WV',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'West Virginia',
			ianaTimeZone: 'America/New_York',
			knownEntries: ['wv', 'us-wv', 'west virginia'],
			blockDate: Date.parse('2025-11-11T02:30:00.000-04:00'),
			blockAllPlayAfterDays: 7,
			blockRedemptionsAfterDays: 14,
		},
		{
			isoEntry: 'TN',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'Tennessee',
			ianaTimeZone: 'America/Chicago',
			knownEntries: ['tn', 'us-tn', 'tennessee'],
			blockDate: Date.parse('2025-11-24T00:30:00.000-06:00'),
			blockAllPlayAfterDays: 29,
			blockRedemptionsAfterDays: 57,
		},
		{
			isoEntry: 'ME',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'Maine',
			ianaTimeZone: 'America/New_York',
			knownEntries: ['me', 'us-me', 'maine'],
			blockDate: Date.parse('2026-07-13T02:30:00.000-04:00'),
			blockAllPlayAfterDays: 0,
			blockRedemptionsAfterDays: 29,
		},
		{
			isoEntry: 'OK',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'Oklahoma',
			ianaTimeZone: 'America/Chicago',
			knownEntries: ['ok', 'us-ok', 'oklahoma'],
			blockDate: Date.parse('2026-10-30T01:30:00.000-05:00'),
			blockAllPlayAfterDays: 0,
			blockRedemptionsDate: Date.parse('2026-11-26T00:00:00.000-06:00'),
		},
	],
	GEOBLOCKED_MARKETS: [
		{
			isoEntry: 'DE',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'Delaware',
			ianaTimeZone: 'America/New_York',
			knownEntries: ['de', 'us-de', 'delaware'],
			blockDate: Date.parse('2025-04-02T21:30:00.000-04:00'), // TZ for DE is EDT (UTC-04:00)
			blockAllPlayAfterDays: 0, // M1 and M2 exit at the same time
			blockLoginAfterDays: 112, // 2025-07-23T21:30:00.000-04:00
		},
		{
			isoEntry: 'AB',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Alberta',
			ianaTimeZone: 'America/Edmonton',
			knownEntries: ['ca-ab', 'ab', 'alberta'],
			blockDate: Date.parse('2025-08-28T01:30:00.000-06:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'BC',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'British Columbia',
			ianaTimeZone: 'America/Vancouver',
			knownEntries: ['ca-bc', 'bc', 'british columbia'],
			blockDate: Date.parse('2025-08-28T00:30:00.000-07:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'MB',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Manitoba',
			ianaTimeZone: 'America/Winnipeg',
			knownEntries: ['ca-mb', 'mb', 'manitoba'],
			blockDate: Date.parse('2025-08-28T02:30:00.000-05:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'NB',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'New Brunswick',
			ianaTimeZone: 'America/Halifax',
			knownEntries: ['ca-nb', 'nb', 'new brunswick'],
			blockDate: Date.parse('2025-08-28T04:30:00.000-03:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'NL',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Newfoundland and Labrador',
			ianaTimeZone: 'America/St_Johns',
			knownEntries: ['ca-nl', 'nl', 'newfoundland and labrador'],
			blockDate: Date.parse('2025-08-28T05:00:00.000-02:30'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'NT',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Northwest Territories',
			ianaTimeZone: 'America/Yellowknife',
			knownEntries: ['ca-nt', 'nt', 'northwest territories'],
			blockDate: Date.parse('2025-08-28T01:30:00.000-06:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'NS',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Nova Scotia',
			ianaTimeZone: 'America/Halifax',
			knownEntries: ['ca-ns', 'ns', 'nova scotia'],
			blockDate: Date.parse('2025-08-28T04:30:00.000-03:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'NU',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Nunavut',
			ianaTimeZone: 'America/Iqaluit',
			knownEntries: ['ca-nu', 'nu', 'nunavut'],
			blockDate: Date.parse('2025-08-28T03:30:00.000-04:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'ON',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Ontario',
			ianaTimeZone: 'America/Toronto',
			knownEntries: ['ca-on', 'on', 'ontario'],
			blockDate: Date.parse('2025-08-28T03:30:00.000-04:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'PE',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Prince Edward Island',
			ianaTimeZone: 'America/Halifax',
			knownEntries: ['ca-pe', 'pe', 'prince edward island'],
			blockDate: Date.parse('2025-08-28T04:30:00.000-03:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'QC',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Quebec',
			ianaTimeZone: 'America/Toronto',
			knownEntries: ['ca-qc', 'qc', 'quebec'],
			blockDate: Date.parse('2025-08-28T03:30:00.000-04:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'SK',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Saskatchewan',
			ianaTimeZone: 'America/Regina',
			knownEntries: ['ca-sk', 'sk', 'saskatchewan'],
			blockDate: Date.parse('2025-08-28T01:30:00.000-06:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'YT',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Yukon',
			ianaTimeZone: 'America/Whitehorse',
			knownEntries: ['ca-yt', 'yt', 'yukon'],
			blockDate: Date.parse('2025-08-28T00:30:00.000-07:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
	],
	GC_ONLY_MARKETS: [
		{
			isoEntry: 'ID',
			knownEntries: ['id', 'us-id', 'idaho'],
			blockDate: Date.UTC(2022, 8, 1, 0, 0),
		},
	],
	CASINO_ONLY_BLOCKED_MARKETS: [INDIANA_CASINO_ONLY_BLOCKED_MARKET],
	BANK_VERIFICATION: {
		enabled: true,
		useCanary: false,
		users: [
			4049230, 4054104, 4050187, 38189, 4054019, 4051806, 4050771, 4038770, 4048953, 4053302, 4049040, 4050276, 4050277,
			4038775, 4054100, 4054197, 4054210, 4060353, 4054902,
		],
	},
	USE_MIGRATED_PLAYER_NOTES: false,
	ANIMATED_DAILY_BONUS_ENABLED: false,
	REDEEMABLE_BALANCE: {
		enabled: true,
		betaGroupCubeiaIds: [156008, 149466, 149645, 158328],
		excludedGroupUserIds: [],
		canarySeed: 0,
		canaryPercent: 100,
	},
	MOBILE_BANNER_EXPERIMENT: {
		enabled: true,
		useCanary: true,
		users: [],
	},
	NEW_DAILY_REWARD_FEATURE_ENABLED: true,
	BRAZE_FEATURES: {
		POKER_BANNER_ENABLED: true,
		CASINO_BANNER_ENABLED: true,
		LOGO_BANNER_ENABLED: true,
		IN_APP_MESSAGES_ENABLED: true,
		INBOX_ENABLED: true,
		TOURNAMENT_FILTER_DEEP_LINK_ENABLED: true,
		SNG_DEEP_LINK_ENABLED: true,
		JSNG_DEEP_LINK_ENABLED: true,
		RING_GAME_DEEP_LINK_ENABLED: true,
	},
	ENABLE_SNG_TABLES_TAB: true,
	POKER_LOBBY_GATEWAY: {
		mode: PokerLobbyToggleMode.ENABLED,
		sngEnabled: true,
	},
	POKER_LOBBY_PLAYER_GAMES: {
		mode: PokerLobbyToggleMode.ENABLED,
	},
	POKER_LOBBY_INSTANCE: {
		mode: PokerLobbyToggleMode.ENABLED,
	},
	GAP_BJK_PREMIUM_GAMEPLAY_HISTORY_ENABLED: true,
	SLOTS_CLOSE_BUTTON_POSITION_ENABLED: false,
	SLOT_GAMES_CLOSE_BUTTON_POSITION_ENABLED: [],
	PLAYER_FEATURE_CONFIG: {
		enabled: true,
		phase1UserIds: ['4060215', '4060334', '4059463', '4059590', '4062227', '4062226', '4062527', '4062533'],
		phase2UserIds: [],
		phase3UserIds: [],
	},
	POKER_SERVER_RING_GAMES_ENABLED: true,
	POKER_SERVER_RING_GAMES_PLAY_DURING_DOWNTIME_ENABLED: true,
	CHALLENGE_CONFIG_RELEASE_AUTOMATION: {
		imageBaseUrl: 'https://assets.qc.test.globalpoker.com',
	},
	FIND_GAME: {
		enabled: true,
		loggingEnabled: true,
	},
	RECENTLY_PLAYED_FEATURE_ENABLED: true,
	FIRST_TIME_OFFER_ENABLED: true,
	PARTIAL_REDEMPTIONS_ENABLED: true,
	PAZE_PAYMENT_ENABLED: true,
	GAP_IN_HOUSE_GAMES_ENABLED: true,
	CLIENT_TRIGGERS: {
		firstGameExitTriggerEnabled: false,
		cashierModalOpenedTriggerEnabled: false,
	},
	POKER_CHALLENGE_UI_DISABLED: true,
	GAP_CARIBBEAN_POKER_GAME_HISTORY_ENABLED: true,
	DELAWARE_MARKET_EXIT_MODAL_ENABLED: true,
	REACT_GAME_TABS_ENABLED: false,
	GAP_GAME_TILE_ASSET_ENABLED: true,
	POKER_LOBBY_SORTING: false,
	GC_ONLY_MARKETS_CHALLENGE_FILTER_ENABLED: true,
	HIGH_HAND_CHALLENGE_ENABLED: false,
	POKER_SERVER_SCHEDULED_TOURNAMENTS_ENABLED: true,
	SOCIAL_LIVE_CASINO_LIVESTAT_FEATURE_ENABLED: true,
	FULL_MARKET_EXIT_MODALS_ENABLED: true,
	SOCIAL_LIVE_CASINO_LIVESTAT_SHOW_MINIMUM_LIMIT: false,
	ALL_IN_OR_FOLD_ENABLED: true,
	BRAZE_DAILY_BONUS_CUSTOM_EVENT_FEATURE_ENABLED: true,
	REFER_A_FRIEND_MENU_ENABLED: true,
	REFERRAL_PROGRAM_ENABLED: true,
	TIERED_REFERRALS_ENABLED: true,
	REFERRAL_NUDGE_ENABLED: true,
	BBJ_FEATURE: {
		mode: BadBeatJackpotToggleMode.ENABLED,
		staffUserIds: [4062107, 4060306, 4060314, 4059996, 4059995, 4060003, 4051387],
		staffRingGamePrefix: 'STAFF_BBJ',
	},
	BBJ_PREVIOUS_LEADERBOARDS_ENABLED: true,
	KYC_FIRST_PURCHASE_NEW_ENDPOINT_ENABLED: true,
	TOOLBAR_V2_ENABLED: false,
	LOBBY_SKELETON_ENABLED: true,
	SC_BLOCKED_US_MARKETS: [
		'US-ID',
		'US-NY',
		'US-LA',
		'US-NJ',
		'US-MS',
		'US-WV',
		'US-TN',
		'US-WA',
		'US-QC',
		'US-MI',
		'US-MT',
		'US-CT',
		'US-NV',
		'US-DE',
		'US-CA',
	],
	KYC_FIRST_PURCHASE_NEW_FLOW_ENABLED: true,
	GEO_COMPLY: {
		canaryPercent: 100,
		canarySeed: 0,
		betaGroupUserIds: [4066470, 4066458, 4066449, 4066492],
		excludedGroupUserIds: [],
	},
	GEO_GATE_REDIRECT_DELAY_MS: 5000,
	GEO_SDK_TIMEOUT_MS: 60000,
	GEO_LOCATE_DEVICE_TIMEOUT_MS: 120000,
	GEO_CALLBACK_FAILED_THRESHOLD: 1,
	GEO_CALLBACK_RESTRICTED_THRESHOLD: 1,
	GEO_CALLBACK_ALLOWED_THRESHOLD: 1,
	GEO_COMPLY_SPOOF_USER_IDS: [],
	GEO_COMPLY_SPOOF_EMAIL_PATTERN: '@vgw\\.co$',
	CDD_RESIDENTIAL_ADDRESS_ENABLED: true,
	CASINO_EXIT_SERVER_CHECK_ENABLED: true,
	GEO_DEBUG_PANEL_ENABLED: true,
	GEO_SIMULATION_ENABLED: false,
	GEO_CUSTOM_GUIDANCE_ENABLED: true,
	GEO_PROTECTION_ENABLED: false,
	JSNG_LEADERBOARDS_ENABLED: true,
	JSNG_SC_MICRO_TIER_ENABLED: true,
	JSNG_PRIZE_HISTORY_ENDPOINT_MIGRATION_ENABLED: true,
	JSNG_RECENT_JACKPOTS_ENABLED: true,
	JSNG_SC5_MULTIPLIER_AND_PRIZE_STRUCTURE_ENABLED: true,
}

export const prodFeatureConfig: FeatureConfig = {
	DAILY_REWARDS_PRIZE_DRAW_FEATURE: {
		testAnimationEnabled: false,
		twoStepFlow: false,
	},
	DAILY_BONUS_EXPERIMENT: {
		enabled: true,
		experimentStartDateTime: Date.parse('2025-09-15T12:00:00.000Z'),
		experimentDays: 14,
		registrationGracePeriodDays: 8,
		userEligibilityWindowDays: 7,
	},
	SURGE_FEATURE: {
		mode: SurgeToggleMode.DISABLED,
		SURGE_URL: 'https://surge-pool.globalpoker.com',
	},
	IS_GLOBAL_CASINO_ENABLED: false,
	PRIVATE_GAMES_FEATURE: {
		url: 'https://private-game.globalpoker.com',
		organiserFee: '25,000',
		expiryConfig: {
			expiryPeriod: 7,
			expiryPeriodUnit: 'days',
		},
	},
	AMOE_CDD_ENABLED: true,
	SELF_EXCLUSIONS_PLAYER_ACCOUNT_ENABLED: true,
	GAP_HUD_ENABLED: false,
	POKER_SERVER_TOURNAMENTS: {
		SNG_ENABLED: true,
		PLAY_DURING_DOWNTIME_ENABLED: true,
		USE_NEW_POKER_SERVER_ONLY: false,
		TIMEOUTS_ENABLED: true,
		HARDCODED_PLAYER_DETAILS: false,
	},
	MFA_ENABLED: true,
	PRIVACY_CHOICE_ENABLED: true,
	BLOCKED_MARKETS: [
		{
			isoEntry: 'MI',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'Michigan',
			ianaTimeZone: 'America/Detroit',
			knownEntries: ['mi', 'us-mi', 'michigan', 'michiga'],
			blockDate: Date.parse('2023-11-01T01:00:00.000-04:00'), // TZ for MI is EDT (UTC-04:00)
			blockAllPlayAfterDays: 30, // 2023-12-01T01:00:00.000-04:00
			blockLoginAfterDays: 92, // 2024-02-01T01:00:00.000-04:00
		},
		{
			isoEntry: 'MT',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'Montana',
			ianaTimeZone: 'America/Denver',
			knownEntries: ['mt', 'us-mt', 'montana'],
			blockDate: Date.parse('2024-01-23T00:00:00.000-07:00'), // TZ for MT is UTC-07:00
			blockAllPlayAfterDays: 30, // 2024-02-22T00:00:00.000-07:00
			blockLoginAfterDays: 90, // 2024-04-22T00:00:00.000-07:00
		},
		{
			isoEntry: 'CT',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'Connecticut',
			ianaTimeZone: 'America/New_York',
			knownEntries: ['ct', 'us-ct', 'connecticut'],
			blockDate: Date.parse('2024-09-16T00:00:00.000-04:00'), // TZ for CT is EDT (UTC-04:00)
			blockAllPlayAfterDays: 30, // 2024-10-16T00:00:00.000-04:00
			blockLoginAfterDays: 91, // 2024-12-16T00:00:00.000-05:00 (EDT ends 5/11/2024)
		},
		{
			isoEntry: 'NV',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'Nevada',
			ianaTimeZone: 'America/Los_Angeles',
			knownEntries: ['nv', 'us-nv', 'nevada'],
			blockDate: Date.parse('2025-01-15T00:00:00.000-08:00'), // TZ for NV is PST (UTC-08:00)
			blockAllPlayAfterDays:
				(Date.parse('2025-02-17T00:00:00.000-08:00') - Date.parse('2025-01-15T00:00:00.000-08:00')) /
				(1000 * 60 * 60 * 24), // 2025-02-17T00:00:00.000-08:00
			blockLoginAfterDays: 90, // 2025-04-15T23:00:00.000-08:00 (PDT starts 9/3/2025, shifts to UCT-0700)
		},
		{
			isoEntry: 'DE',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'Delaware',
			ianaTimeZone: 'America/New_York',
			knownEntries: ['de', 'us-de', 'delaware'],
			blockDate: Date.parse('2025-04-02T21:30:00.000-04:00'), // TZ for DE is EDT (UTC-04:00)
			blockAllPlayAfterDays: 0, // M1 and M2 exit at the same time
			blockLoginAfterDays: 112, // 2025-07-23T21:30:00.000-04:00
		},
		{
			isoEntry: 'AB',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Alberta',
			ianaTimeZone: 'America/Edmonton',
			knownEntries: ['ca-ab', 'ab', 'alberta'],
			blockDate: Date.parse('2025-08-28T01:30:00.000-06:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'BC',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'British Columbia',
			ianaTimeZone: 'America/Vancouver',
			knownEntries: ['ca-bc', 'bc', 'british columbia'],
			blockDate: Date.parse('2025-08-28T00:30:00.000-07:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'MB',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Manitoba',
			ianaTimeZone: 'America/Winnipeg',
			knownEntries: ['ca-mb', 'mb', 'manitoba'],
			blockDate: Date.parse('2025-08-28T02:30:00.000-05:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'NB',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'New Brunswick',
			ianaTimeZone: 'America/Halifax',
			knownEntries: ['ca-nb', 'nb', 'new brunswick'],
			blockDate: Date.parse('2025-08-28T04:30:00.000-03:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'NL',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Newfoundland and Labrador',
			ianaTimeZone: 'America/St_Johns',
			knownEntries: ['ca-nl', 'nl', 'newfoundland and labrador'],
			blockDate: Date.parse('2025-08-28T05:00:00.000-02:30'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'NT',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Northwest Territories',
			ianaTimeZone: 'America/Yellowknife',
			knownEntries: ['ca-nt', 'nt', 'northwest territories'],
			blockDate: Date.parse('2025-08-28T01:30:00.000-06:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'NS',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Nova Scotia',
			ianaTimeZone: 'America/Halifax',
			knownEntries: ['ca-ns', 'ns', 'nova scotia'],
			blockDate: Date.parse('2025-08-28T04:30:00.000-03:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'NU',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Nunavut',
			ianaTimeZone: 'America/Iqaluit',
			knownEntries: ['ca-nu', 'nu', 'nunavut'],
			blockDate: Date.parse('2025-08-28T03:30:00.000-04:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'ON',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Ontario',
			ianaTimeZone: 'America/Toronto',
			knownEntries: ['ca-on', 'on', 'ontario'],
			blockDate: Date.parse('2025-08-28T03:30:00.000-04:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'PE',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Prince Edward Island',
			ianaTimeZone: 'America/Halifax',
			knownEntries: ['ca-pe', 'pe', 'prince edward island'],
			blockDate: Date.parse('2025-08-28T04:30:00.000-03:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'QC',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Quebec',
			ianaTimeZone: 'America/Toronto',
			knownEntries: ['ca-qc', 'qc', 'quebec'],
			blockDate: Date.parse('2025-08-28T03:30:00.000-04:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'SK',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Saskatchewan',
			ianaTimeZone: 'America/Regina',
			knownEntries: ['ca-sk', 'sk', 'saskatchewan'],
			blockDate: Date.parse('2025-08-28T01:30:00.000-06:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'YT',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Yukon',
			ianaTimeZone: 'America/Whitehorse',
			knownEntries: ['ca-yt', 'yt', 'yukon'],
			blockDate: Date.parse('2025-08-28T00:30:00.000-07:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
	],
	PROMO_PLAY_BLOCKED_MARKETS_ENABLED: true,
	PROMO_PLAY_BLOCKED_MARKETS: [
		{
			isoEntry: 'CA',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'California',
			ianaTimeZone: 'America/Los_Angeles',
			knownEntries: ['ca', 'us-ca', 'california'],
			blockDate: Date.parse('2025-12-30T04:30:00.000-08:00'),
			blockAllPlayAfterDays: 0,
			blockRedemptionsAfterDays: 16,
		},
		{
			isoEntry: 'NY',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'New York',
			ianaTimeZone: 'America/New_York',
			knownEntries: ['ny', 'us-ny', 'new york'],
			blockDate: Date.parse('2025-06-02T02:30:00.000-04:00'),
			blockAllPlayAfterDays: 30,
			blockRedemptionsAfterDays: 60,
		},
		{
			isoEntry: 'LA',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'Louisiana',
			ianaTimeZone: 'America/Chicago',
			knownEntries: ['la', 'us-la', 'louisiana'],
			blockDate: Date.parse('2025-07-17T20:30:00.000-05:00'),
			blockAllPlayAfterDays: 29,
			blockRedemptionsAfterDays: 57,
		},
		{
			isoEntry: 'NJ',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'New Jersey',
			ianaTimeZone: 'America/New_York',
			knownEntries: ['nj', 'us-nj', 'new jersey'],
			blockDate: Date.parse('2025-07-29T02:30:00.000-04:00'),
			blockAllPlayAfterDays: 28,
			blockRedemptionsAfterDays: 57,
		},
		{
			isoEntry: 'MS',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'Mississippi',
			ianaTimeZone: 'America/Chicago',
			knownEntries: ['ms', 'us-ms', 'mississippi'],
			blockDate: Date.parse('2025-07-31T02:30:00.000-04:00'),
			blockAllPlayAfterDays: 14,
			blockRedemptionsAfterDays: 35,
		},
		{
			isoEntry: 'WV',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'West Virginia',
			ianaTimeZone: 'America/New_York',
			knownEntries: ['wv', 'us-wv', 'west virginia'],
			blockDate: Date.parse('2025-11-11T02:30:00.000-04:00'),
			blockAllPlayAfterDays: 7,
			blockRedemptionsAfterDays: 14,
		},
		{
			isoEntry: 'TN',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'Tennessee',
			ianaTimeZone: 'America/Chicago',
			knownEntries: ['tn', 'us-tn', 'tennessee'],
			blockDate: Date.parse('2025-11-24T00:30:00.000-06:00'),
			blockAllPlayAfterDays: 29,
			blockRedemptionsAfterDays: 57,
		},
		{
			isoEntry: 'ME',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'Maine',
			ianaTimeZone: 'America/New_York',
			knownEntries: ['me', 'us-me', 'maine'],
			blockDate: Date.parse('2026-07-13T02:30:00.000-04:00'),
			blockAllPlayAfterDays: 0,
			blockRedemptionsAfterDays: 29,
		},
		{
			isoEntry: 'OK',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'Oklahoma',
			ianaTimeZone: 'America/Chicago',
			knownEntries: ['ok', 'us-ok', 'oklahoma'],
			blockDate: Date.parse('2026-10-30T01:30:00.000-05:00'),
			blockAllPlayAfterDays: 0,
			blockRedemptionsDate: Date.parse('2026-11-26T00:00:00.000-06:00'),
		},
	],
	GEOBLOCKED_MARKETS: [
		{
			isoEntry: 'DE',
			isoCountry: 'US',
			countryName: 'United States',
			regionName: 'Delaware',
			ianaTimeZone: 'America/New_York',
			knownEntries: ['de', 'us-de', 'delaware'],
			blockDate: Date.parse('2025-04-02T21:30:00.000-04:00'), // TZ for DE is EDT (UTC-04:00)
			blockAllPlayAfterDays: 0, // M1 and M2 exit at the same time
			blockLoginAfterDays: 112, // 2025-07-23T21:30:00.000-04:00
		},
		{
			isoEntry: 'AB',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Alberta',
			ianaTimeZone: 'America/Edmonton',
			knownEntries: ['ca-ab', 'ab', 'alberta'],
			blockDate: Date.parse('2025-08-28T01:30:00.000-06:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'BC',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'British Columbia',
			ianaTimeZone: 'America/Vancouver',
			knownEntries: ['ca-bc', 'bc', 'british columbia'],
			blockDate: Date.parse('2025-08-28T00:30:00.000-07:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'MB',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Manitoba',
			ianaTimeZone: 'America/Winnipeg',
			knownEntries: ['ca-mb', 'mb', 'manitoba'],
			blockDate: Date.parse('2025-08-28T02:30:00.000-05:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'NB',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'New Brunswick',
			ianaTimeZone: 'America/Halifax',
			knownEntries: ['ca-nb', 'nb', 'new brunswick'],
			blockDate: Date.parse('2025-08-28T04:30:00.000-03:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'NL',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Newfoundland and Labrador',
			ianaTimeZone: 'America/St_Johns',
			knownEntries: ['ca-nl', 'nl', 'newfoundland and labrador'],
			blockDate: Date.parse('2025-08-28T05:00:00.000-02:30'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'NT',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Northwest Territories',
			ianaTimeZone: 'America/Yellowknife',
			knownEntries: ['ca-nt', 'nt', 'northwest territories'],
			blockDate: Date.parse('2025-08-28T01:30:00.000-06:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'NS',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Nova Scotia',
			ianaTimeZone: 'America/Halifax',
			knownEntries: ['ca-ns', 'ns', 'nova scotia'],
			blockDate: Date.parse('2025-08-28T04:30:00.000-03:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'NU',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Nunavut',
			ianaTimeZone: 'America/Iqaluit',
			knownEntries: ['ca-nu', 'nu', 'nunavut'],
			blockDate: Date.parse('2025-08-28T03:30:00.000-04:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'ON',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Ontario',
			ianaTimeZone: 'America/Toronto',
			knownEntries: ['ca-on', 'on', 'ontario'],
			blockDate: Date.parse('2025-08-28T03:30:00.000-04:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'PE',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Prince Edward Island',
			ianaTimeZone: 'America/Halifax',
			knownEntries: ['ca-pe', 'pe', 'prince edward island'],
			blockDate: Date.parse('2025-08-28T04:30:00.000-03:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'QC',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Quebec',
			ianaTimeZone: 'America/Toronto',
			knownEntries: ['ca-qc', 'qc', 'quebec'],
			blockDate: Date.parse('2025-08-28T03:30:00.000-04:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'SK',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Saskatchewan',
			ianaTimeZone: 'America/Regina',
			knownEntries: ['ca-sk', 'sk', 'saskatchewan'],
			blockDate: Date.parse('2025-08-28T01:30:00.000-06:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
		{
			isoEntry: 'YT',
			isoCountry: 'CA',
			countryName: 'Canada',
			regionName: 'Yukon',
			ianaTimeZone: 'America/Whitehorse',
			knownEntries: ['ca-yt', 'yt', 'yukon'],
			blockDate: Date.parse('2025-08-28T00:30:00.000-07:00'),
			blockAllPlayAfterDays: 28,
			blockLoginAfterDays: 56,
		},
	],
	GC_ONLY_MARKETS: [
		{
			isoEntry: 'ID',
			knownEntries: ['id', 'us-id', 'idaho'],
			blockDate: Date.UTC(2022, 8, 1, 0, 0),
		},
	],
	CASINO_ONLY_BLOCKED_MARKETS: [INDIANA_CASINO_ONLY_BLOCKED_MARKET],
	BANK_VERIFICATION: {
		enabled: true,
		useCanary: false,
		users: [
			2329298, 2737019, 2371427, 2201316, 2200058, 2368698, 2318716, 2741949, 2741950, 2754631, 2737385, 2210773,
			2725529, 1947879, 2486570, 3257909,
		],
	},
	USE_MIGRATED_PLAYER_NOTES: false,
	ANIMATED_DAILY_BONUS_ENABLED: false,
	REDEEMABLE_BALANCE: {
		enabled: true,
		betaGroupCubeiaIds: [
			2431046,
			2667107,
			11285,
			2574889,
			2818408,
			1277665,
			197326,
			217471,
			2072743,
			219121,
			2499013,
			2047049,
			2119531,
			135077,
			2777804,
			2383934,
			208716,
			1170736,
			2587248,
			726869,
			209146,
			2237509,
			2345660,
			1943443,
			2509504,
			2763543, // Aaron's test account
			2407935, // Cloris' account
			2748185, // Rebecca's account
			1947006, // Carissa's account
		],
		excludedGroupUserIds: [],
		canarySeed: 0,
		canaryPercent: 100,
	},
	MOBILE_BANNER_EXPERIMENT: {
		enabled: true,
		useCanary: true,
		users: [],
	},
	NEW_DAILY_REWARD_FEATURE_ENABLED: true,
	BRAZE_FEATURES: {
		POKER_BANNER_ENABLED: true,
		CASINO_BANNER_ENABLED: true,
		LOGO_BANNER_ENABLED: true,
		IN_APP_MESSAGES_ENABLED: true,
		INBOX_ENABLED: true,
		TOURNAMENT_FILTER_DEEP_LINK_ENABLED: true,
		SNG_DEEP_LINK_ENABLED: false,
		JSNG_DEEP_LINK_ENABLED: false,
		RING_GAME_DEEP_LINK_ENABLED: true,
	},
	ENABLE_SNG_TABLES_TAB: true,
	POKER_LOBBY_GATEWAY: {
		mode: PokerLobbyToggleMode.ENABLED,
		sngEnabled: true,
	},
	POKER_LOBBY_PLAYER_GAMES: {
		mode: PokerLobbyToggleMode.ENABLED,
	},
	POKER_LOBBY_INSTANCE: {
		mode: PokerLobbyToggleMode.ENABLED,
	},
	GAP_BJK_PREMIUM_GAMEPLAY_HISTORY_ENABLED: true,
	SLOTS_CLOSE_BUTTON_POSITION_ENABLED: false,
	SLOT_GAMES_CLOSE_BUTTON_POSITION_ENABLED: [],
	PLAYER_FEATURE_CONFIG: {
		enabled: true,
		phase1UserIds: ['3456394', '2916634', '3592839', '3227977', '3271896', '3277094'],
		phase2UserIds: [],
		phase3UserIds: [],
	},
	POKER_SERVER_RING_GAMES_ENABLED: true,
	POKER_SERVER_RING_GAMES_PLAY_DURING_DOWNTIME_ENABLED: true,
	CHALLENGE_CONFIG_RELEASE_AUTOMATION: {
		imageBaseUrl: 'https://assets.globalpoker.com',
	},
	FIND_GAME: {
		enabled: true,
		loggingEnabled: true,
	},
	RECENTLY_PLAYED_FEATURE_ENABLED: true,
	FIRST_TIME_OFFER_ENABLED: true,
	PARTIAL_REDEMPTIONS_ENABLED: true,
	PAZE_PAYMENT_ENABLED: false,
	GAP_IN_HOUSE_GAMES_ENABLED: true,
	CLIENT_TRIGGERS: {
		firstGameExitTriggerEnabled: false,
		cashierModalOpenedTriggerEnabled: false,
	},
	POKER_CHALLENGE_UI_DISABLED: true,
	GAP_CARIBBEAN_POKER_GAME_HISTORY_ENABLED: true,
	DELAWARE_MARKET_EXIT_MODAL_ENABLED: true,
	REACT_GAME_TABS_ENABLED: false,
	GAP_GAME_TILE_ASSET_ENABLED: true,
	POKER_LOBBY_SORTING: false,

	GC_ONLY_MARKETS_CHALLENGE_FILTER_ENABLED: false,
	HIGH_HAND_CHALLENGE_ENABLED: false,
	POKER_SERVER_SCHEDULED_TOURNAMENTS_ENABLED: false,
	SOCIAL_LIVE_CASINO_LIVESTAT_FEATURE_ENABLED: true,
	FULL_MARKET_EXIT_MODALS_ENABLED: true,
	SOCIAL_LIVE_CASINO_LIVESTAT_SHOW_MINIMUM_LIMIT: false,
	ALL_IN_OR_FOLD_ENABLED: true,
	BRAZE_DAILY_BONUS_CUSTOM_EVENT_FEATURE_ENABLED: false,
	REFER_A_FRIEND_MENU_ENABLED: true,
	REFERRAL_PROGRAM_ENABLED: true,
	TIERED_REFERRALS_ENABLED: true,
	REFERRAL_NUDGE_ENABLED: true,
	BBJ_FEATURE: {
		mode: BadBeatJackpotToggleMode.ENABLED,
		staffUserIds: [
			2200058, 2201316, 2228408, 2741949, 2977418, 2741950, 3277204, 3271117, 3420462, 3244121, 3244123, 3244136,
			3862730, 3896696, 3896684, 2463934, 2464273, 2819242, 2962744, 3105241, 3151132, 3240500, 3356122, 3430295,
			3466709, 3584283, 3740672, 3900790, 3104343, 3415748, 3416303, 2371427, 2815538, 2210773, 3041105, 3227978,
			3420280, 3421745,
		],
		staffRingGamePrefix: 'STAFF_BBJ',
	},
	BBJ_PREVIOUS_LEADERBOARDS_ENABLED: true,
	KYC_FIRST_PURCHASE_NEW_ENDPOINT_ENABLED: true,
	TOOLBAR_V2_ENABLED: false,
	LOBBY_SKELETON_ENABLED: true,
	SC_BLOCKED_US_MARKETS: [
		'US-ID',
		'US-NY',
		'US-LA',
		'US-NJ',
		'US-MS',
		'US-WV',
		'US-TN',
		'US-WA',
		'US-QC',
		'US-MI',
		'US-MT',
		'US-CT',
		'US-NV',
		'US-DE',
		'US-CA',
	],
	KYC_FIRST_PURCHASE_NEW_FLOW_ENABLED: true,
	GEO_COMPLY: {
		canaryPercent: 100,
		canarySeed: 0,
		betaGroupUserIds: [],
		excludedGroupUserIds: [],
	},
	GEO_GATE_REDIRECT_DELAY_MS: 5000,
	GEO_SDK_TIMEOUT_MS: 60000,
	GEO_LOCATE_DEVICE_TIMEOUT_MS: 120000,
	GEO_CALLBACK_FAILED_THRESHOLD: 1,
	GEO_CALLBACK_RESTRICTED_THRESHOLD: 1,
	GEO_CALLBACK_ALLOWED_THRESHOLD: 1,
	GEO_COMPLY_SPOOF_USER_IDS: [],
	GEO_COMPLY_SPOOF_EMAIL_PATTERN: '',
	CDD_RESIDENTIAL_ADDRESS_ENABLED: true,
	CASINO_EXIT_SERVER_CHECK_ENABLED: true,
	GEO_DEBUG_PANEL_ENABLED: false,
	GEO_SIMULATION_ENABLED: false,
	GEO_CUSTOM_GUIDANCE_ENABLED: true,
	GEO_PROTECTION_ENABLED: false,
	JSNG_LEADERBOARDS_ENABLED: true,
	JSNG_SC_MICRO_TIER_ENABLED: true,
	JSNG_PRIZE_HISTORY_ENDPOINT_MIGRATION_ENABLED: true,
	JSNG_RECENT_JACKPOTS_ENABLED: true,
	JSNG_SC5_MULTIPLIER_AND_PRIZE_STRUCTURE_ENABLED: true,
}
